<template>
  <div class="treeview-card">
    <v-card class="treeview-card-inner" :class="{'elevation-0': onlyTreeview || hideTabs}">
      <div ref="resizeHandle" class="treeview-card-resize-handle" :class="{ 'mirrored': mirrorResize, 'active': resize.active }"/>
      <v-tabs
        v-if="!onlyTreeview && !hideTabs"
        class="flex-grow-1 flex-shrink-0"
        v-model="visibleTab"
        background-color="topbottomstrip"
        centered
        fixed-tabs
        icons-and-text
      >
        <v-tabs-slider></v-tabs-slider>
        <template v-for="(tree, key) in parsedTrees">
          <v-tab
            v-if="!tree.hideTab"
            :key="key"
            :disabled="!$filters.isTrue(tree.loaded) || $filters.isTrue(tree.loading)"
            @click="tab = getMatchingTreeIndex(key)"
          >
            <span>{{ tree.name }}</span>
            <v-progress-circular
              v-if="$filters.isTrue(tree.loading)"
              indeterminate
              size="24"
            />
            <v-icon
              v-else-if="tree.icon"
              v-text="tree.icon"
            />
          </v-tab>
        </template>
      </v-tabs>

      <template v-for="(tree, key) in parsedTrees">
        <slot
          v-if="activeTreeKey === key"
          :name="`tree.${key}.before-search`"
        />
      </template>

      <v-card-text
        v-if="!onlyTreeview && !activeTreeObject.hideTreeviewOptions"
        class="flex-grow-1 flex-shrink-0"
      >
        <!-- Search field -->
        <v-text-field
          :label="$t('search')"
          clearable
          dense
          hide-details
          outlined
          @input="onSearch"
        >
          <template
            v-if="filters.length || activeTree || activeTreeObject.expandable || activeTreeObject.menu"
            v-slot:append-outer
          >
            <div class="d-flex">
              <slot name="extend-filters" :activeTree="activeTree"/>
              <!-- Filter -->
              <v-menu
                v-if="filters.length"
                :close-on-content-click="false"
                offset-y
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-tooltip content-class="d-block" top>
                    <template v-slot:activator="{ on: tooltip }">
                      <v-btn
                        class="order-1"
                        v-bind="attrs"
                        v-on="{ ...on, ...tooltip }"
                        :class="{'green': hasActiveFilters}"
                        :color="hasActiveFilters ? 'white' : 'light-green darken-2'"
                        icon
                        outlined
                        small
                      >
                        <v-icon small>mdi-filter</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('filter') }}</span>
                  </v-tooltip>
                </template>
                <v-list>
                  <v-list-item-group>
                    <v-list-item
                      v-for="(filter, key) of filters.filter(f => !f.hidden)"
                      :key="key"
                      @click="filter.active = !filter.active"
                    >
                      <v-list-item-action>
                        <v-checkbox
                          :input-value="filter.active"
                          color="primary"
                        />
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>{{
                            $filters.getMatchingTranslation({
                              value: filter.name,
                              locale: $i18n.locale
                            })
                          }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-item-group>
                </v-list>
              </v-menu>
              <!-- Expand/Collapse -->
              <v-tooltip
                v-if="activeTreeObject.expandable"
                content-class="d-block"
                top
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    class="order-2 ml-2"
                    icon
                    outlined
                    small
                    @click="isExpanded = !isExpanded"
                  >
                    <v-icon v-if="!isExpanded" small>mdi-arrow-expand-all</v-icon>
                    <v-icon v-else small>mdi-arrow-collapse-all</v-icon>
                  </v-btn>
                </template>
                <span v-if="!isExpanded">{{ $t('expand') }}</span>
                <span v-else>{{ $t('collapse') }}</span>
              </v-tooltip>
              <!-- Menu -->
              <v-menu
                offset-y
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-show="$filters.isTrue(activeTreeObject.menu)"
                    v-bind="attrs"
                    v-on="on"
                    class="ml-2 order-3"
                    color="grey"
                    small
                    dark
                    icon
                    outlined
                  >
                    <v-icon> mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <slot name="action-buttons"/>
                  <TreeviewEditDialog
                    v-if="userIsProjectAuthor && activeTreeObject.editable"
                    v-on="$listeners"
                    :tree="activeTreeObject"
                  />
                </v-list>
              </v-menu>

            </div>
          </template>
        </v-text-field>
      </v-card-text>

      <v-divider v-if="!onlyTreeview"/>

      <v-tabs-items
        v-model="tab"
        class="flex-grow-0 flex-shrink-1 overflow-scroll"
      >
        <template v-for="(tree, key) of parsedTrees">
          <v-tab-item :key="key" :transition="animation">
            <v-card-text class="treeview-panel-item">

              <v-sheet v-if="$filters.isTrue(tree.loading)">
                <v-skeleton-loader type="list-item-three-line"/>
              </v-sheet>

              <Treeview
                v-else
                :activeItem="activeItem"
                :openId="openId"
                ref="treeview"
                v-on="$listeners"
                :tree="tree"
                :filter="filter"
                :search="search"
                :expand.sync="isExpanded"
                :identifier="key"
                :editable="isEditable(tree)"
                @onDragOver="$emit('onDragOver')"
                @onDragLeave="$emit('onDragLeave')"
                @onDrop="$emit('onDrop')"
                :indicateNoData="indicateNoData"
              >
                <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope">
                  <slot :name="slot" v-bind="scope"/>
                </template>
              </Treeview>

            </v-card-text>
          </v-tab-item>
        </template>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import Treeview from '@/components/Utilities/Treeview/Treeview'
import TreeviewEditDialog from '@/components/Utilities/Treeview/TreeviewEditDialog'
import { isEmpty } from 'lodash'

export default {
  /**
   * Component name.
   */
  name: 'TreeviewPanel',

  /**
   * Required components.
   */
  components: {
    Treeview,
    TreeviewEditDialog
  },

  props: {
    activeItem: {
      type: Object,
      required: false,
      default: () => ({})
    },
    activeTree: {
      type: String,
      required: false,
      default: undefined
    },
    openId: {
      type: String,
      required: false,
      default: undefined
    },
    trees: {
      type: [Array, Object],
      required: true
    },
    filters: {
      type: Array,
      required: false,
      default: () => []
    },
    disableAnimation: {
      type: Boolean,
      required: false,
      default: false
    },
    hideTabs: {
      type: Boolean,
      default: false
    },
    onlyTreeview: {
      type: Boolean,
      required: false,
      default: false
    },
    editable: {
      type: Boolean,
      required: false,
      default: false
    },
    search: {
      type: String,
      required: false,
      default: ''
    },
    indicateNoData: {
      type: Boolean,
      required: false,
      default: false
    },
    mirrorResize: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  /**
   * Data variables.
   */
  data () {
    return {
      tab: 0,
      visibleTab: 0,
      animation: null,
      cachedTrees: {},
      parsedTrees: {},
      backupItems: [],
      draggedItem: null,
      isEmpty,
      searchTimeout: null,
      searchValue: '',
      filterKey: 'statusCode',
      filterTrigger: '0',
      expand: {},
      edit: false,
      resize: {}
    }
  },

  /**
   * Computed variables.
   */
  computed: {
    /**
     * Is expanded.
     */
    isExpanded: {
      get () {
        return !!this.expand?.[this.activeTreeKey]
      },

      set (value) {
        this.$set(this.expand, this.activeTreeKey, !!value)
      }
    },

    /**
     * Check if tree view has active filters.
     */
    hasActiveFilters () {
      return this.filters.filter(filter => filter.active === true).length > 0
    },

    /**
     * Get the active tree.
     */
    activeTreeKey () {
      // Return tree key in base of active tab.
      return Object.keys(this.trees)?.[this.tab] || 0
    },

    /**
     * Get the active tree.
     */
    activeTreeObject () {
      // Get current tree.
      return this.parsedTrees?.[this.activeTreeKey] || {}
    }
  },
  watch: {
    disableAnimation: {
      immediate: true,
      handler (disableAnimation) {
        // Define delay.
        const delay = this.animation !== null
          ? 500
          : 0

        // Wait a bit.
        window.setTimeout(() => {
          // Set animation.
          this.animation = !!disableAnimation === true
            ? false
            : undefined
        }, delay)
      }
    },

    /**
     * Watch active tree.
     */
    activeTree: {
      immediate: true,
      handler (activeTree) {
        // on change tab reset property in tree and start all over with collecting properties (Keyword)
        this.$store.commit('dataset/resetPropertyInTree')
        // Get matching tree index.
        const matchingTreeIndex = Object.keys(this.trees).indexOf(activeTree)
        // Set tab.
        this.tab = matchingTreeIndex >= 0 ? matchingTreeIndex : 0
        // Set visible tab.
        this.visibleTab = this.tab
        // Emit active tree update.
        this.$emit('update:active-tree', this.activeTreeKey)
      }
    },

    /**
     * Watch active tree key.
     */
    activeTreeKey: {
      immediate: false,
      handler (activeTreeKey) {
        // on change concept reset property in tree and start all over with collecting properties (Keyword)
        this.$store.commit('dataset/resetPropertyInTree')
        // Emit active tree update.
        this.$emit('update:active-tree', activeTreeKey)
      }
    },

    /**
     * Watch for search changes.
     */
    search: {
      immediate: false,
      handler: function (search) {
        // Update search value.
        this.searchValue = search
      }
    },

    /**
     * Watch for search changes.
     */
    searchValue: {
      handler: function () {
        this.filterTrigger = String(parseInt(this.filterTrigger) + 1)
        // Check if search value is null.
        if (this.searchValue === null) {
          // Convert search value to empty string.
          this.searchValue = ''
        }
      }
    },

    /**
     * Watch tree changes.
     */
    trees: {
      immediate: true,
      deep: true,
      handler: function (trees) {
        // Loop each available tree.
        for (const [key, tree] of Object.entries(trees)) {
          // Get stringified tree.
          const stringifiedTree = JSON.stringify(trees?.[key])
          // Check if stringified tree is equal with cached tree.
          if (stringifiedTree === this.cachedTrees?.[key]) {
            // Continue current loop.
            continue
          }

          this.cachedTrees[key] = stringifiedTree
          // Get cloned tree.
          this.$set(this.parsedTrees, key, JSON.parse(stringifiedTree))
          // Get split text value.
          const splitTextValue = String(this.parsedTrees[key].text || '').split('.')
          // Set tree text.
          this.$set(this.parsedTrees[key], 'text', (splitTextValue?.[0] || 'name'))
          // Set tree text i18n.
          this.$set(this.parsedTrees[key], 'textI18n', (splitTextValue?.[1] || null))
          // Get split searchable text value.
          const splitSearchableTextValue = String(this.parsedTrees[key].searchableText || '').split('.')
          // Set tree searchable text.
          this.$set(this.parsedTrees[key], 'searchableText', (splitSearchableTextValue?.[0] || splitTextValue?.[0] || null))
          // Set tree searchable text i18n.
          this.$set(this.parsedTrees[key], 'searchableTextI18n', (splitSearchableTextValue?.[1] || splitTextValue?.[1] || null))

          // Define cloneable events.
          const cloneableKeys = ['menu', 'loading', 'loaded', 'editable', 'insertable', 'expandable', 'autoExpand', 'notDroppable', 'onSelect', 'onInsert', 'onEdit', 'onBeforeEdit', 'onAfterEdit', 'filterCallback']
          // Check if tree text is a function.
          if (tree?.text instanceof Function) {
            // Push text key.
            cloneableKeys.push('text')
          }

          // Check if tree searchable text is a function.
          if (tree?.searchableText instanceof Function) {
            // Push text key.
            cloneableKeys.push('searchableText')
          }

          // Loop each cloneable key.
          cloneableKeys.forEach((cloneableKey) => {
            // Clone key on cloned tree.
            this.$set(this.parsedTrees[key], cloneableKey, tree?.[cloneableKey])
          })
        }
      }
    }
  },

  mounted () {
    window.addEventListener('mousemove', this.onResize)
    window.addEventListener('mousedown', this.onResizeStart)
    window.addEventListener('mouseup', this.onResizeEnd)
  },

  beforeDestroy () {
    window.removeEventListener('mousemove', this.onResize)
    window.removeEventListener('mousedown', this.onResizeStart)
    window.removeEventListener('mouseup', this.onResizeEnd)
  },

  methods: {
    onResizeStart (event) {
      if (event.target === this.$refs.resizeHandle) {
        const resize = { ...(this.resize || {}) }
        resize.active = true
        resize.startX = event.clientX
        const parentWidth = this.$el.closest('.container').offsetWidth || this.$el.closest('.v-main__wrap').offsetWidth
        resize.maxWidth = Math.round((parentWidth || 800) * 0.8)
        resize.startWidth = this.$el?.offsetWidth
        this.$set(this, 'resize', resize)
        this.$el.style.pointerEvents = 'none'
        this.$el.style.userSelect = 'none'
      }
    },

    onResizeEnd () {
      const resize = { ...(this.resize || {}) }
      if (resize.active) {
        resize.active = false
        this.$set(this, 'resize', resize)
        this.$el.style.pointerEvents = 'auto'
        this.$el.style.userSelect = 'auto'
      }
    },

    onResize (event) {
      const resize = { ...(this.resize || {}) }
      if (resize.active) {
        event.preventDefault()
        resize.diffX = (event.clientX - resize.startX) * (this.mirrorResize ? -1 : 1)
        resize.diffWidth = resize.startWidth + resize.diffX
        resize.newWidth = Math.min(resize.maxWidth, Math.max(400, resize.diffWidth))
        this.$set(this, 'resize', resize)
        window.requestAnimationFrame(() => {
          this.$el.style.width = `${resize.newWidth}px`
        })
      }
    },

    isEditable (tree) {
      return this.editable && tree.editable
    },

    /**
     * Proxy function for get matching icon.
     * @param item
     * @returns {string}
     */
    getMatchingIcon (item) {
      const treeview = this.$refs.treeview[0] || this.$refs.treeview
      return treeview.getMatchingIcon(item, false)
    },

    /**
     * Get matching tree index.
     * @param key
     * @returns {number}
     */
    getMatchingTreeIndex (key) {
      return Object.keys(this.trees).indexOf(key)
    },

    /**
     * Filter tree.
     */
    filter (tree) {
      return this.filterTreeItems(tree.items, {
        key: tree.key,
        text: tree?.searchableText || tree?.text || '',
        children: tree.children,
        filterCallback: tree.filterCallback
      })
    },

    /**
     * Filter tree items.
     */
    filterTreeItems (items, tree) {
      // Check if items are not an array.
      if (!Array.isArray(items)) {
        // Cast it to an empty array.
        return []
      }

      // Map all items.
      return items.map((item) => {
        // Analyse searchValue
        const searchTextIsOid = /^[0-2](\.(0|[1-9][0-9]*))+$/.test(this.searchValue)
        const searchTextIsShortOid = /^(0|[1-9][0-9]*)$/.test(this.searchValue)

        // Get item text.
        const itemText = tree.text instanceof Function
          ? tree.text(item)
          : item?.[tree.text]

        const parsedItemText = Array.isArray(itemText)
          ? itemText.map(lang => lang['#text']).join(' ')
          : JSON.stringify(itemText)

        // Check if search is matching.
        let isVisible = (parsedItemText)
          ? parsedItemText.toLowerCase().includes(this.searchValue.toLowerCase())
          : !this.searchValue

        const fullOid = item.id || item.ref
        const lastPartOfOid = fullOid?.split('.')?.at(-1)

        if (
          (searchTextIsOid && [item.id, item.ref].includes(this.searchValue)) ||
          (searchTextIsShortOid && lastPartOfOid === this.searchValue)
        ) {
          isVisible = true
        }

        // Get all active filters.
        const activeFilters = this.filters
          .filter(filter => filter.active === true)
          .map(filter => filter.key)

        // Check if active filter is set.
        if (activeFilters.length > 0 && isVisible === true) {
          // Set is visible in base of if filter is matching.
          isVisible = activeFilters.includes(item?.[this.filterKey])
        }

        if (typeof tree.filterCallback === 'function') {
          const childrenArr = item?.[tree.children] || []
          const callbackResult = tree.filterCallback({ item, children: childrenArr })
          isVisible = isVisible && typeof callbackResult === 'boolean' ? callbackResult : isVisible
        }

        // Try to get children.
        const children = this.filterTreeItems(item?.[tree.children], tree)
        // Check if children exists.
        if (children.length > 0) {
          // Set is visible to true.
          isVisible = true
        }

        // Check if is visible is false.
        if (isVisible === false) {
          // Return null.
          return null
        }

        // Return item structure with filtered tree item children.
        const result = { ...item }
        if (tree.children) {
          result[tree.children] = children
        }
        return result
      }).filter((item) => item !== null)
    },

    /**
     * On search event.
     */
    onSearch (search) {
      // Clear timeout.
      clearTimeout(this.searchTimeout)
      // Get search length.
      const length = Math.min(String(search).length, 10)
      // Define default delay.
      let delay = length > 0 ? 2000 : 0
      // Loop search length.
      for (let i = 1; i <= length; i++) {
        // Divide delay.
        delay /= i
      }

      // Register timeout.
      this.searchTimeout = setTimeout(() => {
        // Set search value.
        this.searchValue = search
        this.$emit('search', search)
      }, delay)
    },

    /**
     * Start edit mode.
     */
    startEditMode () {
      // Get tree key in base of active tab.
      const treeKey = Object.keys(this.trees)?.[this.tab] || 0
      // Get current tree.
      const tree = this.trees?.[treeKey] || 0
      // Set backup items.
      this.backupItems = JSON.parse(JSON.stringify(tree?.items))
      // Set edit to true.
      this.edit = true
    },

    /**
     * Cancel edit mode.
     */
    cancelEditMode () {
      // Get tree key in base of active tab.
      const treeKey = Object.keys(this.trees)?.[this.tab] || 0
      // Get current tree.
      const tree = this.trees?.[treeKey] || 0
      // Restore backup items.
      this.$set(tree, 'items', this.backupItems)
      // Set edit to false.
      this.edit = false
    }

  }
}
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/settings/_variables';

@media #{map-get($display-breakpoints, 'sm-and-up')} {
  .treeview-card {
    position: sticky;
    top: 72px;
    left: 0;
    display: flex;
    align-items: flex-start;
    height: 100%;
    width: 400px;

    .treeview-card-inner {
      position: relative;
      width: 100%;
      display: flex;
      flex-direction: column;

      .treeview-card-resize-handle {
        position: absolute;
        right: 0;
        top: 0;
        width: 4px;
        height: 100%;
        cursor: ew-resize;
        z-index: 10;
        transition: background ease-in-out 0.2s;

        &.mirrored {
          right: auto;
          left: 0;
        }

        &.active,
        &:hover {
          background: #88888844;
        }
      }
    }

    .v-tabs .v-tab {
      max-width: none;
    }

    .v-card {
      max-height: calc(100vh - 170px);
    }
  }

  .overflow-scroll {
    overflow-y: auto;
  }
}
</style>
